import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight,
  ArrowUpRight,
  Download,
  Sparkles,
  Quote,
  CheckCircle2,
  Calendar,
  Inbox,
  Bell,
  AlertCircle,
  Smartphone,
  ListChecks,
} from "lucide-react";
import { api } from "@/lib/api";
import Seo from "@/components/Seo";
import { MagneticButton } from "@/components/MagneticButton";
import { PRODUCTS } from "@/data/products";

/* ─────────────────────────── Small building blocks ─────────────────────────── */

// Masked line-by-line reveal for headline words/lines
function MaskLine({ children, delay = 0, className = "" }) {
  return (
    <span className={`reveal-mask ${className}`}>
      <motion.span
        className="reveal-line"
        initial={{ y: "110%" }}
        animate={{ y: "0%" }}
        transition={{ duration: 1.05, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.span>
    </span>
  );
}

// Fade / rise reveal on scroll
function Reveal({ children, delay = 0, y = 24, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.85, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ─────────────────────────── Content data ─────────────────────────── */

const chapters = [
  {
    n: "01",
    kicker: "The reality",
    title: "You're not behind. The system is.",
    body:
      "Schools talk to parents through ten different channels; portal, app, email, folder, group chat; and no one hands you a manual for that. What looks like forgetting is really a system problem.",
    tags: ["Missed picture day", "Forgot Spirit Week", "Lost the field-trip form"],
  },
  {
    n: "02",
    kicker: "The system",
    title: "A calm rhythm, not another app.",
    body:
      "One home for every date, contact, and form. A fifteen-minute Sunday reset that catches the week before it catches you. Templates that turn 'I'll remember' into 'It's already on the calendar.'",
    tags: ["Command Center", "Weekly Reset", "Parent Radar"],
  },
  {
    n: "03",
    kicker: "The proof",
    title: "Built by a parent. Tested in real life.",
    body:
      "Built by Lisa, parent and psychology grad, after too many missed picture days. Practical, non-judgmental, and refined against real elementary-school chaos.",
    tags: ["Print + digital", "Free lifetime updates", "Instant download"],
  },
];

const problems = [
  { icon: AlertCircle, text: "Missed Picture Day (again)" },
  { icon: Calendar, text: "Forgot Spirit Week themes" },
  { icon: Inbox, text: "Lost track of school emails" },
  { icon: Bell, text: "Last-minute field trip forms" },
  { icon: Smartphone, text: "Too many school apps" },
  { icon: ListChecks, text: "Constantly feeling behind" },
];

// Editorial marquee copy
const marqueeItems = [
  "The I Missed Picture Day System",
  "The Elementary School Command Center",
  "The Weekly 15-Minute Reset",
  "The Elementary School Survival Kit",
  "Parent Radar Check",
];

/* ─────────────────────────── Page ─────────────────────────── */

export default function HomePage() {
  const [form, setForm] = useState({ first_name: "", email: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const heroRef = useRef(null);
  const reduce = useReducedMotion();

  // Parallax on hero image + info card
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -80]);
  const heroScale = useTransform(scrollYProgress, [0, 1], [1, reduce ? 1 : 1.06]);
  const stickerY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 40]);

  useEffect(() => {
    // reserve viewport height calc, nothing else
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim() || !form.email.trim()) {
      toast.error("Please add your first name and email.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/subscribers", { ...form, source: "homepage-lead-magnet" });
      setDone(true);
      toast.success("You're in! Check your inbox for the Weekly Reset.");
      setForm({ first_name: "", email: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="home-page" className="relative">
      <Seo
        title=""
        description="Simple systems, planning tools, and practical strategies to help busy elementary school parents stay organized and stress less, without another app to manage."
        path="/"
        keywords="elementary school parent organization, school planning for parents, school event tracker, parent organization system, executive function for parents, elementary school survival guide, family organization tools"
      />

      {/* ═══════════════════════════════ HERO ═══════════════════════════════ */}
      <section
        ref={heroRef}
        data-testid="hero-section"
        className="relative overflow-hidden pt-14 md:pt-20 pb-24 md:pb-32"
      >
        {/* Editorial background, soft tinted washes */}
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute inset-0 bg-brand-bg" />
          <div className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-brand-sage/12 blur-[110px]" />
          <div className="absolute top-1/3 -left-32 w-[440px] h-[440px] rounded-full bg-brand-accent/10 blur-[110px]" />
          <div className="absolute inset-0 grain opacity-40" />
        </div>

        {/* Top eyebrow row */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-inkMuted">
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="flex items-center gap-2"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-brand-accent" />
              Volume 01, for elementary school parents
            </motion.span>
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.25 }}
              className="hidden md:inline text-brand-inkMuted/80"
            >
              An editorial from One Step Ahead
            </motion.span>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8 md:mt-14">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
            {/* Headline, masked line-by-line reveal */}
            <div className="lg:col-span-8 relative">
              <h1
                data-testid="hero-headline"
                className="font-display font-medium tracking-[-0.03em] text-brand-ink leading-[0.92] text-[46px] xs:text-[56px] sm:text-[74px] md:text-[92px] lg:text-[112px]"
              >
                <span className="block"><MaskLine delay={0.05}>Stay one</MaskLine></span>
                <span className="block"><MaskLine delay={0.18}>step ahead</MaskLine></span>
                <span className="block relative">
                  <MaskLine delay={0.32}>
                    of{" "}
                    <span className="font-editorial italic font-normal text-brand-sage">
                      elementary
                    </span>
                  </MaskLine>
                </span>
                <span className="block">
                  <MaskLine delay={0.44}>
                    <span className="font-editorial italic font-normal text-brand-sage">school</span>{" "}
                    chaos.
                  </MaskLine>
                </span>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="mt-10 max-w-xl text-base sm:text-lg text-brand-inkSoft leading-relaxed"
              >
                Simple systems, planning tools, and practical strategies for busy parents;
                so you spend less time chasing school updates, and more time actually being with your kid.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.85, ease: [0.16, 1, 0.3, 1] }}
                className="mt-8 flex flex-wrap items-center gap-3"
              >
                <MagneticButton>
                  <Link
                    to="/resources#subscribe"
                    data-testid="hero-primary-cta"
                    className="group inline-flex items-center gap-3 bg-brand-ink text-brand-bg hover:bg-brand-sageDark transition-colors px-7 py-4 rounded-full font-medium shadow-soft"
                  >
                    <Download className="h-4 w-4" strokeWidth={1.75} />
                    Get the Free Weekly Reset
                    <span className="inline-block transition-transform group-hover:translate-x-0.5">
                      <ArrowRight className="h-4 w-4" strokeWidth={2} />
                    </span>
                  </Link>
                </MagneticButton>
                <MagneticButton strength={14}>
                  <Link
                    to="/products"
                    data-testid="hero-secondary-cta"
                    className="inline-flex items-center gap-2 text-brand-ink hover:text-brand-sageDark transition-colors px-5 py-4 font-medium underline underline-offset-8 decoration-brand-sage/50 hover:decoration-brand-sageDark decoration-2"
                  >
                    Explore the system
                    <ArrowUpRight className="h-4 w-4" strokeWidth={2} />
                  </Link>
                </MagneticButton>
              </motion.div>
            </div>

            {/* Editorial hero image, clipped, spotlit, parallax */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.1, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-4 lg:sticky lg:top-24"
            >
              <div className="relative">
                {/* number label */}
                <div className="absolute -top-6 -left-2 md:-left-4 z-20 select-none">
                  <span className="font-editorial italic text-brand-accent text-6xl md:text-7xl leading-none">
                    01
                  </span>
                </div>

                {/* image */}
                <motion.div
                  style={{ y: heroY, scale: heroScale }}
                  className="spotlight-frame relative rounded-[28px] overflow-hidden aspect-[4/5] w-full bg-brand-sage/10"
                >
                  <img
                    src="/images/home-hero.png"
                    alt="A monthly calendar, planner, and pens laid out on a marble desk"
                    className="h-full w-full object-cover"
                    loading="eager"
                  />
                  {/* subtle sage vignette to blend into palette */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-brand-ink/10 via-transparent to-transparent" />
                </motion.div>

                {/* floating info sticker */}
                <motion.div
                  style={{ y: stickerY }}
                  className="absolute -bottom-6 -left-6 md:-left-10 bg-brand-bg border border-brand-border rounded-2xl px-5 py-4 shadow-soft max-w-[260px] hidden sm:block"
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-brand-sage">
                    <span className="h-1.5 w-1.5 rounded-full bg-brand-accent animate-pulse" />
                    This week
                  </div>
                  <p className="mt-2 text-sm text-brand-ink leading-snug">
                    Tue, <span className="font-editorial italic">Picture Day.</span> Fri, Crazy Hair Spirit Day.
                  </p>
                </motion.div>

                {/* editorial caption right */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.8, delay: 1.25 }}
                  className="absolute -right-3 top-6 rotate-90 origin-top-right hidden lg:block"
                >
                  <span className="text-[10px] uppercase tracking-[0.3em] text-brand-inkMuted">
                    Fig. 01, a calmer command center
                  </span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Small scroll hint */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.4 }}
          className="mt-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-3 text-[11px] uppercase tracking-[0.28em] text-brand-inkMuted"
        >
          <span className="h-px w-14 bg-brand-inkMuted/40" />
          Read on, three short chapters
        </motion.div>
      </section>

      {/* ═══════════════════════════════ MARQUEE ═══════════════════════════════ */}
      <section
        aria-hidden="true"
        data-testid="editorial-marquee"
        className="relative border-y border-brand-border bg-brand-bg overflow-hidden py-6 md:py-8"
      >
        <div className="marquee-track flex items-center gap-14 whitespace-nowrap will-change-transform">
          {[...marqueeItems, ...marqueeItems, ...marqueeItems].map((t, i) => (
            <span key={i} className="flex items-center gap-14">
              <span className="font-editorial italic text-brand-ink text-4xl md:text-6xl lg:text-7xl leading-none">
                {t}
              </span>
              <span className="text-brand-accent text-3xl md:text-5xl leading-none">✳</span>
            </span>
          ))}
        </div>
      </section>

      {/* ═══════════════════════════════ CHAPTERS (MANIFESTO) ═══════════════════════════════ */}
      <section data-testid="chapters-section" className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-inkMuted">
              The manifesto, in three chapters
            </p>
          </Reveal>

          <div className="mt-14 md:mt-20 space-y-24 md:space-y-32">
            {chapters.map((c, idx) => (
              <div key={c.n} className="grid grid-cols-12 gap-6 md:gap-10 items-start">
                {/* chapter number */}
                <Reveal delay={0.05} className="col-span-12 md:col-span-3">
                  <div className="flex md:block items-baseline gap-4">
                    <span className="font-editorial italic text-brand-accent text-[96px] md:text-[140px] leading-[0.85]">
                      {c.n}
                    </span>
                    <span className="md:mt-4 block text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-sage">
                      {c.kicker}
                    </span>
                  </div>
                </Reveal>

                {/* chapter content */}
                <Reveal delay={0.15} className="col-span-12 md:col-span-9 md:pl-8 md:border-l md:border-brand-border">
                  <h2
                    data-testid={`chapter-title-${idx}`}
                    className="font-display font-medium tracking-[-0.02em] text-brand-ink text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.02]"
                  >
                    {c.title.split(" ").map((w, i) =>
                      /system|calm|proof|not|behind|real|life\./i.test(w) ? (
                        <span key={i} className="font-editorial italic font-normal text-brand-sage">
                          {w}{" "}
                        </span>
                      ) : (
                        <span key={i}>{w} </span>
                      )
                    )}
                  </h2>
                  <p className="mt-6 max-w-2xl text-base md:text-lg text-brand-inkSoft leading-relaxed">
                    {c.body}
                  </p>
                  <div className="mt-8 flex flex-wrap gap-2">
                    {c.tags.map((t) => (
                      <span
                        key={t}
                        className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] uppercase tracking-[0.18em] font-semibold text-brand-ink bg-white border border-brand-border"
                      >
                        <span className="h-1 w-1 rounded-full bg-brand-accent" />
                        {t}
                      </span>
                    ))}
                  </div>
                </Reveal>
              </div>
            ))}
          </div>

          {/* Problems belt, a quiet coda under chapter 01/02 */}
          <Reveal delay={0.1} className="mt-24 md:mt-28">
            <div className="border-t border-brand-border pt-10">
              <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-inkMuted">
                Does any of this sound familiar?
              </p>
              <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {problems.map((p, i) => (
                  <motion.div
                    key={p.text}
                    initial={{ opacity: 0, y: 12 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    data-testid={`problem-card-${i}`}
                    className="group flex flex-col justify-between gap-8 rounded-2xl border border-brand-border bg-white p-5 hover:bg-brand-sage hover:border-brand-sage transition-colors duration-300 min-h-[130px]"
                  >
                    <p.icon
                      className="h-5 w-5 text-brand-sage group-hover:text-white transition-colors"
                      strokeWidth={1.5}
                    />
                    <p className="text-sm text-brand-ink group-hover:text-white transition-colors leading-snug">
                      {p.text}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════ PRODUCT SPOTLIGHT ═══════════════════════════════ */}
      <section
        data-testid="product-spotlight"
        className="relative bg-brand-ink text-brand-bg overflow-hidden py-24 md:py-32"
      >
        <div className="absolute inset-0 grain opacity-20 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <Reveal>
              <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-sage">
                The system, in three parts
              </p>
              <h2 className="mt-4 font-display font-medium tracking-[-0.02em] text-4xl sm:text-5xl md:text-6xl leading-[1.02] max-w-2xl">
                Print + digital <span className="font-editorial italic text-brand-accent">templates</span> that do the remembering for you.
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <MagneticButton strength={16}>
                <Link
                  to="/products"
                  data-testid="spotlight-see-all"
                  className="inline-flex items-center gap-2 text-sm font-medium text-brand-bg/90 hover:text-brand-bg underline underline-offset-8 decoration-brand-sage decoration-2"
                >
                  See all products <ArrowUpRight className="h-4 w-4" />
                </Link>
              </MagneticButton>
            </Reveal>
          </div>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {PRODUCTS.map((p, i) => (
              <Reveal key={p.slug} delay={i * 0.1}>
                <Link
                  to={`/products/${p.slug}`}
                  data-testid={`spotlight-product-${i}`}
                  className={`group block relative ${i === 1 ? "md:mt-16" : ""} ${i === 2 ? "md:mt-8" : ""}`}
                >
                  <div className="relative aspect-[4/5] overflow-hidden rounded-[20px] bg-white/5 border border-white/10">
                    <img
                      src={p.cover}
                      alt={p.name}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.06]"
                    />
                    {/* spotlight */}
                    <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/70 via-brand-ink/10 to-transparent" />
                    {/* number */}
                    <span className="absolute top-4 left-5 font-editorial italic text-brand-accent text-4xl leading-none">
                      0{i + 1}
                    </span>
                    {/* price */}
                    <span className="absolute top-5 right-5 text-[11px] uppercase tracking-[0.22em] font-semibold text-brand-bg/90 bg-white/10 border border-white/20 rounded-full px-3 py-1 backdrop-blur">
                      ${p.price}
                    </span>
                    {/* footer */}
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <p className="text-[10px] uppercase tracking-[0.28em] text-brand-sage font-semibold">
                        {p.tagline}
                      </p>
                      <h3 className="mt-2 font-display text-xl md:text-2xl font-medium leading-snug text-brand-bg">
                        {p.name}
                      </h3>
                      <span className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-brand-bg/85 group-hover:text-brand-accent transition-colors">
                        Read the system <ArrowRight className="h-3.5 w-3.5" />
                      </span>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════ TESTIMONY ═══════════════════════════════ */}
      <section data-testid="home-testimonials" className="py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-inkMuted">
              04, In their own words
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <figure
              data-testid="testimonial-card-placeholder"
              className="mt-10 md:mt-14 relative rounded-[28px] bg-white border border-brand-border p-10 md:p-16"
            >
              <Quote className="h-9 w-9 text-brand-sage" strokeWidth={1.25} />
              <blockquote className="mt-6 font-display font-medium tracking-[-0.01em] text-brand-ink text-3xl sm:text-4xl md:text-5xl leading-[1.08]">
                Real parent stories, <span className="font-editorial italic text-brand-sage">coming soon.</span>
                <span className="block mt-2 text-brand-inkSoft font-normal text-lg sm:text-xl md:text-2xl font-sans leading-snug">
                  Tried the system? <Link to="/contact" className="underline underline-offset-4 decoration-brand-sage decoration-2 hover:text-brand-ink">Share your story</Link> and you might be featured in this space.
                </span>
              </blockquote>
              <figcaption className="mt-10 flex items-center gap-4">
                <span className="h-px w-14 bg-brand-inkMuted/40" />
                <span className="text-[11px] uppercase tracking-[0.28em] text-brand-inkMuted">
                  Be one of the first
                </span>
              </figcaption>
            </figure>
          </Reveal>
        </div>
      </section>

      {/* ═══════════════════════════════ LEAD MAGNET ═══════════════════════════════ */}
      <section id="lead-magnet" data-testid="lead-magnet-section" className="px-4 sm:px-6 lg:px-8 pb-24">
        <Reveal>
          <div className="max-w-6xl mx-auto rounded-[32px] overflow-hidden bg-gradient-to-br from-brand-sage to-brand-sageDark text-white relative">
            <div className="absolute inset-0 grain opacity-25 pointer-events-none" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 relative">
              <div className="p-10 md:p-14">
                <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-white/80">
                  05, Free download
                </p>
                <h2 className="mt-4 font-display font-medium tracking-[-0.02em] text-4xl sm:text-5xl leading-[1.02]">
                  The Weekly{" "}
                  <span className="font-editorial italic font-normal">15-minute</span>{" "}
                  School Reset.
                </h2>
                <p className="mt-5 text-white/90 leading-relaxed max-w-md">
                  A one-page ritual you can run every Sunday, so you catch what&apos;s coming
                  before it surprises you.
                </p>
                <ul className="mt-8 space-y-3 text-white/95 text-sm">
                  {[
                    "Scan school portal + email + family calendar",
                    "Capture events, forms, and themes for the week",
                    "Decide once, not seven times",
                  ].map((t, i) => (
                    <li key={t} className="flex items-start gap-3">
                      <span className="mt-0.5 font-editorial italic text-brand-accent text-lg leading-none">0{i + 1}</span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="p-10 md:p-14 bg-white/10 backdrop-blur-sm border-t md:border-t-0 md:border-l border-white/15">
                {done ? (
                  <div data-testid="lead-magnet-success" className="h-full flex flex-col justify-center">
                    <CheckCircle2 className="h-10 w-10 text-white" />
                    <h3 className="mt-4 font-display text-3xl font-medium leading-tight">
                      You&apos;re on the <span className="font-editorial italic">list.</span>
                    </h3>
                    <p className="mt-2 text-white/90">
                      The Weekly Reset is on its way. While you wait,{" "}
                      <Link to="/resources" className="underline underline-offset-4">explore more parent resources</Link>.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-4" data-testid="lead-magnet-form">
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white/80">
                        First name
                      </label>
                      <input
                        data-testid="lead-magnet-name"
                        value={form.first_name}
                        onChange={(e) => setForm({ ...form, first_name: e.target.value })}
                        type="text"
                        placeholder="Jamie"
                        className="mt-2 w-full bg-white/95 text-brand-ink placeholder:text-brand-inkMuted rounded-full px-5 py-3.5 outline-none focus:ring-2 ring-white"
                        required
                      />
                    </div>
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.22em] font-semibold text-white/80">
                        Email
                      </label>
                      <input
                        data-testid="lead-magnet-email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        type="email"
                        placeholder="jamie@email.com"
                        className="mt-2 w-full bg-white/95 text-brand-ink placeholder:text-brand-inkMuted rounded-full px-5 py-3.5 outline-none focus:ring-2 ring-white"
                        required
                      />
                    </div>
                    <MagneticButton className="!block w-full" strength={12}>
                      <button
                        type="submit"
                        data-testid="lead-magnet-submit"
                        disabled={loading}
                        className="w-full inline-flex items-center justify-center gap-2 bg-white text-brand-sageDark hover:bg-brand-bg transition-colors px-6 py-4 rounded-full font-semibold disabled:opacity-70"
                      >
                        {loading ? "Sending..." : "Send Me the Free Checklist"}
                        {!loading && <ArrowRight className="h-4 w-4" strokeWidth={2} />}
                      </button>
                    </MagneticButton>
                    <p className="text-xs text-white/75">
                      No spam. Just practical school tips, unsubscribe anytime.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      {/* ═══════════════════════════════ FOUNDER QUOTE ═══════════════════════════════ */}
      <section data-testid="founder-quote" className="pb-32">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-inkMuted">
              06, A note from the founder
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-10 font-editorial italic text-brand-ink text-3xl sm:text-4xl md:text-5xl leading-[1.15]">
              &ldquo;You&apos;re not behind. The schools just talk to parents through ten different channels, and no one taught you a system for that.&rdquo;
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <p className="mt-10 flex items-center justify-center gap-3 text-[11px] uppercase tracking-[0.28em] font-semibold text-brand-inkMuted">
              <span className="inline-block h-px w-10 bg-brand-inkMuted/40" />
              Lisa
              <Sparkles className="h-3 w-3 text-brand-accent" />
              Founder, One Step Ahead Parents
              <span className="inline-block h-px w-10 bg-brand-inkMuted/40" />
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
